// @ts-nocheck
/**
 * Role Hierarchy Page
 * Epic #17: RBAC & Permissions Platform Services
 *
 * Dedicated page for viewing and interacting with the role hierarchy visualization
 */

import React from 'react';
import { Grid, Column, Breadcrumb, BreadcrumbItem, Tile } from '@carbon/react';
import { Link } from 'react-router-dom';
import RoleHierarchyVisualization from '../../components/admin/RoleHierarchyVisualization';
import styles from './RoleHierarchyPage.module.scss';

const RoleHierarchyPage: React.FC = () => {
  return (
    <Grid fullWidth className={styles.roleHierarchyPage}>
      {/* Breadcrumb Navigation */}
      <Column lg={16} className={styles.breadcrumbContainer}>
        <Breadcrumb noTrailingSlash>
          <BreadcrumbItem>
            <Link to="/admin">Admin</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link to="/admin/rbac">RBAC Dashboard</Link>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>Role Hierarchy</BreadcrumbItem>
        </Breadcrumb>
      </Column>

      {/* Page Header */}
      <Column lg={16}>
        <div className={styles.pageHeader}>
          <h1>Role Hierarchy Visualization</h1>
          <p className={styles.description}>
            Interactive tree diagram showing the educational role hierarchy and permission inheritance. Click on any
            role to view detailed information about permissions and assigned users.
          </p>
        </div>
      </Column>

      {/* Legend */}
      <Column lg={16}>
        <Tile className={styles.legendTile}>
          <h3>Role Hierarchy Legend</h3>
          <div className={styles.legendItems}>
            <div className={styles.legendItem}>
              <div className={`${styles.colorCircle} ${styles.roleColorLevel10}`} />
              <span className={styles.label}>Platform Admin (Level 10)</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.colorCircle} ${styles.roleColorLevel9}`} />
              <span className={styles.label}>District Admin (Level 9)</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.colorCircle} ${styles.roleColorLevel7}`} />
              <span className={styles.label}>Principal (Level 7)</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.colorCircle} ${styles.roleColorLevel5}`} />
              <span className={styles.label}>Teacher (Level 5)</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.colorCircle} ${styles.roleColorLevel3}`} />
              <span className={styles.label}>Creator (Level 3)</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.colorCircle} ${styles.roleColorLevel2}`} />
              <span className={styles.label}>Parent (Level 2)</span>
            </div>
            <div className={styles.legendItem}>
              <div className={`${styles.colorCircle} ${styles.roleColorLevel1}`} />
              <span className={styles.label}>Student (Level 1)</span>
            </div>
          </div>
        </Tile>
      </Column>

      {/* Visualization */}
      <Column lg={16}>
        <Tile className={styles.visualizationTile}>
          <RoleHierarchyVisualization height={600} width={1200} />
        </Tile>
      </Column>

      {/* Instructions */}
      <Column lg={16}>
        <Tile className={styles.instructionsTile}>
          <h4>How to Use This Visualization</h4>
          <ul>
            <li>Click on any role circle to view detailed permissions and user counts</li>
            <li>Use the zoom controls to adjust the view (zoom in/out or reset)</li>
            <li>Hover over role nodes to highlight them</li>
            <li>The number inside each circle represents the hierarchy level</li>
            <li>Lines connect roles showing permission inheritance paths</li>
          </ul>
        </Tile>
      </Column>
    </Grid>
  );
};

export default RoleHierarchyPage;
